// Iter152 · Split extraído de OfertasComercial.jsx (líneas 880–1611).
// Contiene PendingMotivoModal, ImportFromExternalModal, OfertaForm, F, I, KPIcard.

import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Check, ChevronDown, Globe, Link2, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";
import OfertaMergeSuggestionsPanel from "@/components/offer/OfertaMergeSuggestionsPanel";
import LicitacionTimeline from "@/components/offer/LicitacionTimeline";
import ComplianceMatrix from "@/components/offer/ComplianceMatrix";
import ProvenancePanel from "@/components/offer/ProvenancePanel";
import CompletitudPanel from "@/components/offer/CompletitudPanel";
import { MOTIVOS_NO_PRESENTACION, ACTIVIDADES_POR_DG, ALL_ACTIVIDADES, ZONA_GERENCIAS, fmtFechaOferta, CAN_DELETE_ROLES } from "./shared";

function PendingMotivoModal({ onClose, onResolved }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMotivo, setSelectedMotivo] = useState({});  // {id: motivo}
  const [comentarios, setComentarios] = useState({});         // {id: text}
  const [saving, setSaving] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ofertas-comercial/pending-motivo-no-presentacion`);
      setPending(r.data || []);
    } catch { toast.error("Error cargando pendientes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const confirmar = async (oid) => {
    const motivo = selectedMotivo[oid];
    if (!motivo) { toast.error("Selecciona un motivo"); return; }
    setSaving(oid);
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/resolve-motivo-no-presentacion`, {
        motivo_no_presentacion: motivo,
        comentario: comentarios[oid] || "",
      });
      toast.success("Motivo guardado");
      setPending(p => p.filter(x => x.id !== oid));
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando motivo");
    } finally { setSaving(null); }
  };

  const revertir = async (oid, target) => {
    if (!window.confirm(`¿Revertir esta oferta a ${target}? La auto-transición se cancelará.`)) return;
    setSaving(oid);
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/resolve-motivo-no-presentacion`, {
        motivo_no_presentacion: "Profitability",  // placeholder, no se usa cuando revertir_a viene
        revertir_a: target,
      });
      toast.success(`Revertida a ${target}`);
      setPending(p => p.filter(x => x.id !== oid));
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error revirtiendo");
    } finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="motivo-modal"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2 text-rose-700">
              <AlertTriangle size={16}/> Motivo de NO PRESENTACIÓN pendiente
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Estas ofertas estaban en <strong>PUBLICADA</strong> o <strong>POR DECIDIR</strong> y su fecha de presentación ha pasado.
              La plataforma las ha movido automáticamente a <strong>NO PRESENTADA</strong>. Indica el motivo o
              revierte la transición.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? <p className="text-center text-gray-400 text-sm py-8"><Loader2 size={16} className="inline animate-spin mr-2"/>Cargando…</p>
            : pending.length === 0 ? <p className="text-center text-emerald-600 italic py-8">✓ Sin pendientes. Todo al día.</p>
            : pending.map(o => (
              <div key={o.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50" data-testid={`pending-row-${o.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 line-clamp-2" title={o.objeto_contrato}>
                      {o.objeto_contrato || o.cliente || "—"}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      <strong>{o.cliente}</strong> · Fecha pres. {fmtFechaOferta(o.fecha_presentacion)}
                      {o.presupuesto_licitacion_ute_mm ? ` · ${o.presupuesto_licitacion_ute_mm.toFixed(2)} M€` : ""}
                      <span className="ml-2 inline-flex items-center gap-1 bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">
                        Antes: {o.auto_state_previous || "—"}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    value={selectedMotivo[o.id] || ""}
                    onChange={e => setSelectedMotivo(m => ({...m, [o.id]: e.target.value}))}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                    data-testid={`motivo-select-${o.id}`}>
                    <option value="">— Selecciona motivo —</option>
                    {MOTIVOS_NO_PRESENTACION.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="Comentario opcional…"
                    value={comentarios[o.id] || ""}
                    onChange={e => setComentarios(c => ({...c, [o.id]: e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1 flex-1 min-w-[140px]"
                    data-testid={`motivo-comment-${o.id}`}/>
                  <button
                    onClick={() => confirmar(o.id)}
                    disabled={saving === o.id || !selectedMotivo[o.id]}
                    data-testid={`motivo-confirm-${o.id}`}
                    className="text-[11px] px-3 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 font-semibold flex items-center gap-1">
                    {saving === o.id ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>}
                    Confirmar NO PRESENTADA
                  </button>
                  <button
                    onClick={() => revertir(o.id, o.auto_state_previous || "PUBLICADA")}
                    disabled={saving === o.id}
                    data-testid={`motivo-revert-${o.id}`}
                    title="Cancela la auto-transición y devuelve la oferta al estado anterior"
                    className="text-[11px] px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 flex items-center gap-1">
                    <RefreshCw size={10}/> Revertir
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// Iter303 · C03/E03 — Confirmar presentación de ofertas ESTUDIO vencidas.
// Vencer el plazo NUNCA marca PRESENTADA por sí solo: requiere que un
// responsable confirme con justificante, registre una ampliación de plazo,
// o marque NO PRESENTADA con motivo.
function PendingConfirmacionPresentacionModal({ onClose, onResolved }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState({});           // {id: "presentada"|"ampliacion"|"no_presentada"}
  const [justificante, setJustificante] = useState({});
  const [nuevaFecha, setNuevaFecha] = useState({});
  const [motivo, setMotivo] = useState({});
  const [comentario, setComentario] = useState({});
  const [saving, setSaving] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ofertas-comercial/pending-confirmacion-presentacion`);
      setPending(r.data || []);
    } catch { toast.error("Error cargando pendientes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const resolver = async (oid) => {
    const m = mode[oid];
    if (!m) { toast.error("Selecciona qué ocurrió con esta oferta"); return; }
    const payload = { presentada: m === "presentada" };
    if (m === "presentada") {
      payload.justificante_ref = justificante[oid] || "";
    } else if (m === "ampliacion") {
      if (!nuevaFecha[oid]) { toast.error("Indica la nueva fecha de presentación"); return; }
      payload.nueva_fecha_presentacion = nuevaFecha[oid];
      payload.comentario = comentario[oid] || "";
    } else {
      if (!motivo[oid]) { toast.error("Selecciona un motivo"); return; }
      payload.motivo_no_presentacion = motivo[oid];
      payload.comentario = comentario[oid] || "";
    }
    setSaving(oid);
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/resolve-confirmacion-presentacion`, payload);
      toast.success("Confirmación guardada");
      setPending(p => p.filter(x => x.id !== oid));
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando confirmación");
    } finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="confirmacion-presentacion-modal"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2 text-amber-700">
              <AlertTriangle size={16}/> Confirmar presentación (plazo vencido)
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Estas ofertas están en <strong>ESTUDIO</strong> y su fecha de presentación ya pasó.
              Vencer el plazo <strong>no es evidencia</strong> de que se presentó: confirma qué ocurrió
              realmente con cada una.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? <p className="text-center text-gray-400 text-sm py-8"><Loader2 size={16} className="inline animate-spin mr-2"/>Cargando…</p>
            : pending.length === 0 ? <p className="text-center text-emerald-600 italic py-8">✓ Sin pendientes. Todo al día.</p>
            : pending.map(o => (
              <div key={o.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50" data-testid={`confirmacion-row-${o.id}`}>
                <p className="text-xs font-semibold text-gray-900 line-clamp-2" title={o.objeto_contrato}>
                  {o.objeto_contrato || o.cliente || "—"}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  <strong>{o.cliente}</strong> · Vencida desde {fmtFechaOferta(o.fecha_presentacion_vencida_desde || o.fecha_presentacion)}
                  {o.presupuesto_licitacion_ute_mm ? ` · ${o.presupuesto_licitacion_ute_mm.toFixed(2)} M€` : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[
                    ["presentada", "✓ Se presentó (con justificante)"],
                    ["ampliacion", "↻ Hubo ampliación de plazo"],
                    ["no_presentada", "✗ No se presentó"],
                  ].map(([k, label]) => (
                    <button key={k}
                      onClick={() => setMode(m => ({...m, [o.id]: k}))}
                      data-testid={`confirmacion-mode-${k}-${o.id}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold ${
                        mode[o.id] === k ? "bg-[#007934] text-white border-[#007934]" : "border-gray-300 text-gray-600 hover:bg-gray-100"
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                {mode[o.id] === "presentada" && (
                  <input type="text" placeholder="Referencia del justificante de entrega (opcional)"
                    value={justificante[o.id] || ""}
                    onChange={e => setJustificante(j => ({...j, [o.id]: e.target.value}))}
                    data-testid={`confirmacion-justificante-${o.id}`}
                    className="mt-2 text-xs border border-gray-200 rounded px-2 py-1 w-full"/>
                )}
                {mode[o.id] === "ampliacion" && (
                  <div className="mt-2 flex gap-2">
                    <input type="date" value={nuevaFecha[o.id] || ""}
                      onChange={e => setNuevaFecha(n => ({...n, [o.id]: e.target.value}))}
                      data-testid={`confirmacion-nueva-fecha-${o.id}`}
                      className="text-xs border border-gray-200 rounded px-2 py-1"/>
                    <input type="text" placeholder="Comentario opcional…"
                      value={comentario[o.id] || ""}
                      onChange={e => setComentario(c => ({...c, [o.id]: e.target.value}))}
                      className="text-xs border border-gray-200 rounded px-2 py-1 flex-1"/>
                  </div>
                )}
                {mode[o.id] === "no_presentada" && (
                  <div className="mt-2 flex gap-2">
                    <select value={motivo[o.id] || ""}
                      onChange={e => setMotivo(m => ({...m, [o.id]: e.target.value}))}
                      data-testid={`confirmacion-motivo-${o.id}`}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
                      <option value="">— Selecciona motivo —</option>
                      {MOTIVOS_NO_PRESENTACION.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <input type="text" placeholder="Comentario opcional…"
                      value={comentario[o.id] || ""}
                      onChange={e => setComentario(c => ({...c, [o.id]: e.target.value}))}
                      className="text-xs border border-gray-200 rounded px-2 py-1 flex-1"/>
                  </div>
                )}
                {mode[o.id] && (
                  <button onClick={() => resolver(o.id)} disabled={saving === o.id}
                    data-testid={`confirmacion-guardar-${o.id}`}
                    className="mt-2 text-[11px] px-3 py-1 rounded bg-[#007934] text-white hover:bg-[#006528] disabled:opacity-40 font-semibold flex items-center gap-1">
                    {saving === o.id ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>}
                    Guardar confirmación
                  </button>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MODAL DE IMPORTACIÓN DESDE OPORTUNIDADES EXTERNAS
// ═══════════════════════════════════════════════════════════════════════════
function ImportFromExternalModal({ onClose, onImported }) {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ min_score: 70, dg: "all", actividad: "all", source: "all", q: "", only_fresh: true });
  const [importing, setImporting] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100, min_score: filters.min_score });
      if (filters.dg !== "all")        params.append("direccion_general", filters.dg);
      if (filters.actividad !== "all") params.append("actividad", filters.actividad);
      if (filters.source !== "all")    params.append("source", filters.source);
      if (filters.only_fresh)          params.append("only_fresh", "true");
      const { data } = await axios.get(`${API}/tender360/m0/external-opportunities?${params}`);
      let result = data || [];
      if (filters.q) {
        const qLow = filters.q.toLowerCase();
        result = result.filter(o => (o.title || "").toLowerCase().includes(qLow) || (o.organism || "").toLowerCase().includes(qLow));
      }
      // Filtrar las que ya están convertidas a oferta
      result = result.filter(o => !o.converted_to_oferta_id);
      setOps(result);
    } catch { toast.error("Error cargando externas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filters.min_score, filters.dg, filters.actividad, filters.source, filters.only_fresh]);

  // Reset actividad si cambia DG y la actividad ya no es válida
  useEffect(() => {
    if (filters.dg === "all") return;
    const allowed = ACTIVIDADES_POR_DG[filters.dg] || [];
    if (filters.actividad !== "all" && !allowed.includes(filters.actividad)) {
      setFilters(f => ({ ...f, actividad: "all" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dg]);

  const importOp = async (o) => {
    setImporting(o.external_id);
    try {
      await axios.post(`${API}/ofertas-comercial/import-from-external`, { external_id: o.external_id });
      toast.success(`Oferta creada desde ${o.source.toUpperCase()} · score ${o.ai_score} · revisa y completa los campos`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error importando");
    } finally { setImporting(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] flex flex-col" onClick={e=>e.stopPropagation()} data-testid="oc-import-modal">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                <Globe size={16} className="text-purple-600"/> Importar desde Plataformas de Contratación
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">PLACSP · BOE · TED EU · pre-seleccionadas y puntuadas por Pando. Click en &quot;Importar&quot; para crear la oferta con todos los campos posibles.</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input placeholder="Filtrar por título / organismo" value={filters.q} onChange={e=>setFilters(f=>({...f,q:e.target.value}))}
                   className="text-xs border border-gray-200 rounded px-2 py-1.5 flex-1 min-w-[180px]" data-testid="import-search"/>
            <select value={filters.min_score} onChange={e=>setFilters(f=>({...f,min_score:parseInt(e.target.value,10)}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="import-min-score">
              <option value={85}>Score Pando ≥ 85 (top)</option>
              <option value={70}>Score Pando ≥ 70</option>
              <option value={50}>Score Pando ≥ 50</option>
              <option value={0}>Todas</option>
            </select>
            <select value={filters.dg} onChange={e=>setFilters(f=>({...f,dg:e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white font-semibold" data-testid="import-dg">
              <option value="all">Toda DG</option>
              <option value="SERVICIOS">DG Servicios</option>
              <option value="TRATAMIENTO">DG Tratamiento</option>
            </select>
            <select value={filters.actividad} onChange={e=>setFilters(f=>({...f,actividad:e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="import-actividad"
                    title={filters.dg === "all" ? "Actividad" : `Actividades de ${filters.dg}`}>
              <option value="all">Toda actividad</option>
              {(filters.dg === "all" ? ALL_ACTIVIDADES : (ACTIVIDADES_POR_DG[filters.dg] || []))
                .map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filters.source} onChange={e=>setFilters(f=>({...f,source:e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="import-source">
              <option value="all">PLACSP + BOE + TED</option>
              <option value="placsp">Solo PLACSP</option>
              <option value="boe">Solo BOE</option>
              <option value="ted">Solo TED EU</option>
            </select>
            <label
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded cursor-pointer border ${
                filters.only_fresh
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                  : "bg-gray-50 text-gray-500 border-gray-200"
              }`}
              title="Solo publicaciones de hoy/ayer y con fecha de presentación abierta"
              data-testid="import-only-fresh">
              <input
                type="checkbox"
                checked={filters.only_fresh}
                onChange={e => setFilters(f => ({...f, only_fresh: e.target.checked}))}
                className="accent-emerald-600"
              />
              Solo recientes
            </label>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? <p className="text-center text-gray-400 text-sm py-8"><Loader2 size={16} className="inline animate-spin mr-2"/>Cargando…</p>
            : ops.length === 0 ? <p className="text-center text-gray-400 italic py-8">Sin oportunidades sin importar con esos filtros</p>
            : (
              <div className="space-y-1.5">
                {ops.filter(o => !filters.q || (o.title||"").toLowerCase().includes(filters.q.toLowerCase()) || (o.organism||"").toLowerCase().includes(filters.q.toLowerCase())).map((o, i) => (
                  <div key={o.external_id || i} className="flex items-start gap-3 p-2 rounded border border-gray-100 hover:bg-gray-50">
                    <div className={`w-9 h-9 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      o.ai_score >= 85 ? "bg-green-100 text-green-700" :
                      o.ai_score >= 70 ? "bg-emerald-100 text-emerald-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{o.ai_score}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 line-clamp-1" title={o.title}>{o.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-1">{o.ai_area || "?"}</span>
                        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-1">{o.source}</span>
                        {o.organism && <span className="text-gray-400">· {o.organism.slice(0, 60)}</span>}
                        {o.publication_date && <span className="text-gray-400">· pub {o.publication_date}</span>}
                        {o.deadline && <span className="text-rose-500 ml-1">· deadline {o.deadline}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {o.value_eur != null && <span className="text-xs font-bold text-gray-700">{(o.value_eur/1e6).toFixed(2)} M€</span>}
                      {o.url && <a href={o.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline">pliego</a>}
                      <button onClick={()=>importOp(o)} disabled={importing === o.external_id}
                              data-testid={`import-btn-${i}`}
                              className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50 font-semibold">
                        {importing === o.external_id ? <Loader2 size={11} className="animate-spin"/> : <ArrowRight size={11}/>} Importar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FORMULARIO UNIFICADO CON SUB-BLOQUES CONDICIONALES
// ═══════════════════════════════════════════════════════════════════════════

// Iter260 · "Vincular Proyecto al Firmar" — solo se muestra cuando la oferta
// está ganada (fase FIRMADA/ADJUDICADA) y no tiene proyecto vinculado
// (`auto_project_id` vacío). Nunca automático: siempre requiere clic.
function ProjectLinkModal({ oferta, onClose, onLinked }) {
  const [mode, setMode] = useState(null); // "crear" | "vincular"
  const [adoProjects, setAdoProjects] = useState([]);
  const [selectedAdo, setSelectedAdo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/ado-projects-list`)
      .then(r => setAdoProjects(r.data.projects || []))
      .catch(() => setAdoProjects([]));
  }, []);

  const crearInterno = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/ofertas-comercial/${oferta.id}/create-project`);
      toast.success(data.created ? "✓ Proyecto de seguimiento creado" : "Ya existía un proyecto vinculado");
      onLinked(data.project);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear el proyecto");
    } finally { setSaving(false); }
  };

  const vincularAdo = async () => {
    if (!selectedAdo) { toast.error("Selecciona un proyecto ADO"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/ofertas-comercial/${oferta.id}/link-ado-project`, { azure_devops_project: selectedAdo });
      toast.success(`✓ Vinculado a "${selectedAdo}"`);
      onLinked(data.project);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al vincular el proyecto");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" data-testid="project-link-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm text-gray-800">Vincular proyecto</h3>
          <button onClick={onClose} data-testid="project-link-modal-close"><X size={16} className="text-gray-400 hover:text-gray-600"/></button>
        </div>
        {!mode && (
          <div className="space-y-2">
            <button onClick={() => setMode("crear")} data-testid="project-link-mode-crear"
                    className="w-full text-left text-xs border border-gray-200 rounded-lg p-3 hover:border-emerald-300 hover:bg-emerald-50">
              <div className="font-semibold text-gray-800">Crear proyecto de seguimiento</div>
              <div className="text-gray-500 mt-0.5">Proyecto interno para seguir esta oferta ganada (sin sincronizar con Azure DevOps).</div>
            </button>
            <button onClick={() => setMode("vincular")} data-testid="project-link-mode-vincular"
                    className="w-full text-left text-xs border border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:bg-indigo-50">
              <div className="font-semibold text-gray-800">Vincular a proyecto Azure DevOps real</div>
              <div className="text-gray-500 mt-0.5">Elige entre los ~{adoProjects.length} proyectos ADO ya sincronizados.</div>
            </button>
          </div>
        )}
        {mode === "crear" && (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">Se creará un proyecto interno de seguimiento vinculado a esta oferta.</p>
            <div className="flex gap-2">
              <button onClick={() => setMode(null)} className="text-xs px-3 py-1.5 rounded border border-gray-300">Atrás</button>
              <button onClick={crearInterno} disabled={saving} data-testid="project-link-confirm-crear"
                      className="flex-1 text-xs font-semibold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin"/> : null} Crear proyecto
              </button>
            </div>
          </div>
        )}
        {mode === "vincular" && (
          <div className="space-y-3">
            <select value={selectedAdo} onChange={e => setSelectedAdo(e.target.value)} data-testid="project-link-ado-select"
                    className="w-full text-xs border border-gray-300 rounded px-2 py-2">
              <option value="">— selecciona proyecto ADO —</option>
              {adoProjects.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={() => setMode(null)} className="text-xs px-3 py-1.5 rounded border border-gray-300">Atrás</button>
              <button onClick={vincularAdo} disabled={saving || !selectedAdo} data-testid="project-link-confirm-vincular"
                      className="flex-1 text-xs font-semibold px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin"/> : null} Vincular
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectLinkBanner({ oferta, onLinked }) {
  const [open, setOpen] = useState(false);
  const fase = String(oferta.fase_adjudicacion || "").toUpperCase();
  const isGanada = fase === "FIRMADA" || fase === "ADJUDICADA";
  if (!isGanada || oferta.auto_project_id) return null;
  return (
    <div className="mx-4 mt-3 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="project-link-banner">
      <div className="text-[11px] text-amber-800">
        <span className="font-semibold">Oferta ganada sin proyecto vinculado.</span> Crea un proyecto de seguimiento o vincúlalo a Azure DevOps.
      </div>
      <button onClick={() => setOpen(true)} data-testid="project-link-banner-btn"
              className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700">
        Vincular proyecto
      </button>
      {open && <ProjectLinkModal oferta={oferta} onClose={() => setOpen(false)} onLinked={(project) => { setOpen(false); onLinked(project); }} />}
    </div>
  );
}

function OfertaForm({ data, onClose, onSaved, onDeleted }) {
  const { user } = useAuth();
  const canDelete = !!user && CAN_DELETE_ROLES.includes(user.role);
  const [form, setForm] = useState(() => ({
    // defaults seguros
    pais: "ESPAÑA", empresa_g_valoriza: "VSM", empresa_oferta: "VSM",
    porcentaje_empresa_g_valoriza: 1, procedimiento: "CONCURSO ABIERTO",
    direccion_general: "SERVICIOS", tipo_cliente: "Público", tipo_contratacion: "NC",
    estado_licitacion: "ESTUDIO", market: "Addreseble", central_zona: "CENTRAL",
    ...data,
  }));
  const [catalogs, setCatalogs] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("base");   // base | sub | comentarios
  const isNew = !data?.id;
  // Iter304 · D02 Fase 2 — "Fuentes de esta licitación" (modo sombra, solo lectura)
  const [fuentes, setFuentes] = useState(null);
  const [showFuentes, setShowFuentes] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/catalogs`).then(r => setCatalogs(r.data));
    // Iter176 · Cargar valores para autocompletado (datalists)
    axios.get(`${API}/ofertas-comercial/autocomplete`)
      .then(r => setAutocomplete(r.data))
      .catch(() => setAutocomplete({}));
    // Iter304 · Cargar fuentes cruzadas (si el admin ya corrió el rebuild canónico)
    if (!isNew && data?.id) {
      axios.get(`${API}/ofertas-comercial/${data.id}/fuentes`)
        .then(r => setFuentes(r.data))
        .catch(() => setFuentes(null));
    }
  }, []);

  const update = (k, v) => setForm(p => ({...p, [k]: v}));

  const handleDelete = async () => {
    const label = form.cliente ? `"${form.cliente}"${form.expediente ? ` (exp. ${form.expediente})` : ""}` : "esta oferta";
    if (!window.confirm(`¿Enviar ${label} a la papelera?\n\nNo se borra definitivamente: podrás restaurarla desde la papelera.`)) return;
    try {
      await axios.delete(`${API}/ofertas-comercial/${data.id}`);
      toast.success("Movida a la papelera — se puede restaurar");
      onDeleted ? onDeleted() : (onSaved ? onSaved() : onClose());
    } catch { toast.error("No se pudo eliminar"); }
  };

  const save = async () => {
    if (!form.cliente) { toast.error("El cliente es obligatorio"); return; }

    // Iter151/176 · Guard UTE incompleta (bloqueante).
    // Si el usuario marca UTE, debe: (a) tener % VSM > 0, y (b) si el % VSM
    // es menor al 100% (es decir, hay socios externos reales), tener al
    // menos 2 socios definidos en el editor visual que sumen exactamente
    // 100% (compilados a `empresa_oferta` "VSM/FCC/Prezero").
    // Iter288 · Si % VSM = 100 (UTE formal pero 100% Valoriza, sin socios
    // externos), NO se exige el formato "VSM/Socio" — "VSM" sola es válida.
    if (form.ute) {
      const pct = Number(form.porcentaje_ute);
      const empresa = String(form.empresa_oferta || "").trim();
      const requiereSocios = pct > 0 && pct < 100;
      const socios = !requiereSocios || (/[-/]/.test(empresa) && empresa.length > 3);
      const missing = [];
      if (!(pct > 0)) missing.push("% VSM en la UTE");
      if (!socios) missing.push("empresas socias en 'Empresa oferta' (usa el editor UTE debajo)");
      // Validar suma 100% del editor de socios (si existe)
      const partners = parseUtePartners(empresa, form.porcentaje_ute);
      const sum = partners.reduce((s, p) => s + (parseFloat(p.pct) || 0), 0);
      if (partners.length >= 2 && Math.abs(sum - 100) > 0.01) {
        missing.push(`la suma de % de los socios (${sum.toFixed(1)}%) debe ser 100%`);
      }
      if (missing.length) {
        toast.error(`UTE marcada pero faltan datos: ${missing.join(" · ")}`, { duration: 6000 });
        return;
      }
    }

    setSaving(true);
    try {
      // Coerce numerics
      const payload = { ...form };
      ["presupuesto", "presupuesto_licitacion_ute_mm", "presupuesto_anual_ute",
       "porcentaje_empresa_g_valoriza", "presupuesto_ofertado",
       "porcentaje_ute", "importe_con_ute",
       "apertura_puntuacion_pliego_tec", "apertura_puntuacion_pliego_eco",
       "apertura_puntuacion_vsm_tec", "apertura_puntuacion_vsm_eco", "apertura_puntuacion_vsm_tot",
       "apertura_puntuacion_winner_tec", "apertura_puntuacion_winner_eco", "apertura_puntuacion_winner_tot",
       "apertura_maxima_puntuacion_tec", "apertura_maxima_puntuacion_eco",
       "apertura_presupuesto_ofertado",
       "ue_capex", "ue_ebitda", "ue_gross_margin", "ue_irr_unlevered", "ue_moic", "ue_ebt",
      ].forEach(k => {
        if (payload[k] !== undefined && payload[k] !== "" && payload[k] !== null) {
          payload[k] = parseFloat(payload[k]);
          if (isNaN(payload[k])) payload[k] = null;
        } else if (payload[k] === "") payload[k] = null;
      });
      ["duracion_anios", "prorroga", "num_licitadores",
       "apertura_num_total_licitadores",
       "apertura_posicion_vsm_tec", "apertura_posicion_vsm_eco", "apertura_posicion_vsm_tot",
      ].forEach(k => {
        if (payload[k] !== undefined && payload[k] !== "" && payload[k] !== null) {
          payload[k] = parseInt(payload[k]);
          if (isNaN(payload[k])) payload[k] = null;
        } else if (payload[k] === "") payload[k] = null;
      });

      // Iter180 · Validación UX: FIRMADA + fecha de firma futura o vacía es
      // incoherente. Se bloquea también en backend (422), pero la damos aquí
      // para mensaje más rápido y accesible.
      const isFirmada = payload.estado_licitacion === "FIRMADA" || payload.fase_adjudicacion === "FIRMADA";
      if (isFirmada) {
        const ff = String(payload.fecha_firma_contrato || "").trim().slice(0, 10);
        if (!ff) {
          toast.error("Para marcar FIRMADA hay que indicar la fecha de firma del contrato.");
          setSaving(false);
          return;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ff)) {
          toast.error(`Fecha de firma inválida (${ff}).`);
          setSaving(false);
          return;
        }
        // Comparación por cadena en hora LOCAL para evitar el desfase de zona
        // horaria: new Date("YYYY-MM-DD") se interpreta en UTC y hacía que la
        // fecha de hoy se detectara como "futura" en España (UTC+1/+2).
        const n = new Date();
        const todayStr = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
        if (ff > todayStr) {
          toast.error(`La fecha de firma (${ff}) es posterior a hoy. FIRMADA no admite fecha futura — comprueba si querías la fecha límite de firma.`);
          setSaving(false);
          return;
        }
      }

      if (isNew) {
        await axios.post(`${API}/ofertas-comercial`, payload);
        toast.success("Oferta creada");
      } else {
        await axios.put(`${API}/ofertas-comercial/${data.id}`, payload);
        toast.success("Oferta actualizada");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const estado = form.estado_licitacion;
  const showSubPipeline = estado === "PRESENTADA";
  const showSubNoPres   = estado === "NO PRESENTADA";
  const showApertura    = estado === "PRESENTADA" || ["ADJUDICADA","FIRMADA","ADJUDICADA_OTROS","NO_ADJUDICADA"].includes(form.fase_adjudicacion);
  // Iter185 · Units Economics sólo tiene sentido tras presentar (o con apertura
  // pública). Antes se mostraba también en ESTUDIO — el usuario pidió sacarlo.
  const showUE          = estado === "PRESENTADA" || !!form.apertura_puntuacion_vsm_tot;
  // Iter185 · Winner sólo se muestra al llegar a fases avanzadas de adjudicación
  // (antes aparecía en Observaciones desde el inicio y confundía).
  const showWinner      = ["ADJUDICADA","ADJUDICADA_OTROS","FIRMADA","NO_ADJUDICADA"].includes(form.fase_adjudicacion || "");
  // Iter185/206 · Estados agrupados. En creación (isNew) PRESENTADA sale del
  // grupo de "altas" (no debe crearse una oferta directamente PRESENTADA) —
  // sigue estando disponible en el dropdown de transiciones al editar.
  const ESTADOS_ALTA_NEW   = ["PUBLICADA", "POR DECIDIR", "ESTUDIO"];
  const ESTADOS_ALTA_EDIT  = ["PUBLICADA", "POR DECIDIR", "ESTUDIO", "PRESENTADA"];
  const ESTADOS_ALTA        = isNew ? ESTADOS_ALTA_NEW : ESTADOS_ALTA_EDIT;
  // Feedback 2026-10 · el desplegable "Cambiar estado" también debe permitir
  // volver a los estados de pipeline (PUBLICADA/POR DECIDIR/ESTUDIO), no solo
  // los terminales: desde Publicada/Por decidir se podía pasar a En estudio.
  const ESTADOS_TRANSICION  = isNew
    ? ["PUBLICADA", "POR DECIDIR", "ESTUDIO", "BORRADOR", "PRESENTADA", "NO PRESENTADA", "SUSPENDIDA", "ANULADA", "DESISTIDA", "DESCARTADA"]
    : ["PUBLICADA", "POR DECIDIR", "ESTUDIO", "PRESENTADA", "BORRADOR", "NO PRESENTADA", "SUSPENDIDA", "ANULADA", "DESISTIDA", "DESCARTADA"];
  const ESTADO_COLOR = {
    "BORRADOR":       "bg-orange-100 text-orange-800 border-orange-300",
    "POR DECIDIR":    "bg-amber-100 text-amber-800 border-amber-300",
    "ESTUDIO":        "bg-indigo-100 text-indigo-800 border-indigo-300",
    "PUBLICADA":      "bg-gray-100 text-gray-800 border-gray-300",
    "PRESENTADA":     "bg-blue-100 text-blue-800 border-blue-300",
    "NO PRESENTADA":  "bg-rose-100 text-rose-800 border-rose-300",
    "SUSPENDIDA":     "bg-purple-100 text-purple-800 border-purple-300",
    "ANULADA":        "bg-slate-200 text-slate-700 border-slate-400",
    "DESISTIDA":      "bg-slate-200 text-slate-700 border-slate-400",
    "DESCARTADA":     "bg-red-100 text-red-700 border-red-300",
  };

  if (!catalogs) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()}>
        {/* Iter176 · Datalists globales para autocompletado predictivo. */}
        {autocomplete && (
          <>
            <datalist id="ac-cliente">{(autocomplete.cliente || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-provincia">{(autocomplete.provincia || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-empresa-g-valoriza">{(autocomplete.empresa_g_valoriza || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-empresa-oferta">{(autocomplete.empresas_socias || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-winner">{(autocomplete.winner || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-incumbent">{(autocomplete.incumbent || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-direccion-zona">{(autocomplete.direccion_zona || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-zona">{(autocomplete.zona || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-gerencia">{(() => {
              const z = (form.direccion_zona || "").trim().toUpperCase().replace(/\s+/g, "_");
              const all = autocomplete.gerencia || [];
              const allowed = ZONA_GERENCIAS[z];
              const list = allowed ? all.filter(g => allowed.includes(g)) : all;
              return (list.length ? list : all).map(v => <option key={v} value={v}/>);
            })()}</datalist>
            <datalist id="ac-responsables">{(autocomplete.responsables || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-publicacion">{(autocomplete.publicacion || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-central-zona">{(autocomplete.central_zona || []).map(v => <option key={v} value={v}/>)}</datalist>
            <datalist id="ac-formato-prorrogas">{(autocomplete.formato_prorrogas || []).map(v => <option key={v} value={v}/>)}</datalist>
          </>
        )}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-sm font-bold">{isNew ? "Nueva oferta comercial" : `Editar · ${form.cliente || ""}`}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>

        {/* CC06 · Procedencia de los datos (confirmado/derivado/inferido/desconocido) */}
        {!isNew && <ProvenancePanel oid={data?.id}/>}
        {/* CC03/CC06 · Completar datos (motor + ciclo de propuestas) */}
        {!isNew && <CompletitudPanel oid={data?.id}/>}

        {/* Iter304 · D02 Fase 2 — Fuentes de esta licitación (modo sombra, solo lectura).
            Solo aparece si ya se ejecutó un rebuild canónico Y encontró cruce con
            otras colecciones (external_opportunities / mercado_opportunities). */}
        {fuentes?.found && (
          <div className="border-b border-gray-100 px-4 py-2 bg-indigo-50/60" data-testid="oferta-fuentes-panel">
            <button type="button" onClick={() => setShowFuentes(s => !s)}
              className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1.5"
              data-testid="oferta-fuentes-toggle">
              <Link2 size={12}/>
              Fuentes de esta licitación
              {" · "}
              {(fuentes.fuentes.external_opportunities?.length || 0) + (fuentes.fuentes.mercado_opportunities?.length || 0)} enlazada(s)
              {fuentes.link_confidence === "media" && <span className="ml-1 text-amber-600">(confianza media)</span>}
              <ChevronDown size={11} className={`transition-transform ${showFuentes ? "rotate-180" : ""}`}/>
            </button>
            {showFuentes && (
              <div className="mt-2 space-y-1.5 text-[11px]">
                {(fuentes.fuentes.external_opportunities || []).map(e => (
                  <div key={e.external_id} className="flex items-center gap-1.5 text-gray-700">
                    <span className="text-[9px] font-bold uppercase text-sky-700 bg-sky-100 px-1 rounded">PLACSP/TED</span>
                    {e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="hover:underline truncate max-w-md">{e.title}</a> : <span className="truncate max-w-md">{e.title}</span>}
                  </div>
                ))}
                {(fuentes.fuentes.mercado_opportunities || []).map(m => (
                  <div key={m.id} className="flex items-center gap-1.5 text-gray-700">
                    <span className="text-[9px] font-bold uppercase text-purple-700 bg-purple-100 px-1 rounded">Tender360/Mercado</span>
                    <span className="truncate max-w-md">{m.objeto_contrato || m.objeto}</span>
                    <span className="text-gray-400">({m.estado_licitacion})</span>
                  </div>
                ))}
                {fuentes.fuentes.otras_ofertas_comerciales_mismo_expediente?.length > 0 && (
                  <p className="text-amber-700">⚠ Mismo expediente también presente en {fuentes.fuentes.otras_ofertas_comerciales_mismo_expediente.length} otra(s) oferta(s) comercial(es) — revisar en Cola de Calidad si son lotes distintos.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Iter185 · Barra de Estado destacada en el TOP del modal.
            Muestra los 4 estados de alta como pills clicables y un dropdown
            "Cambiar estado" con las transiciones (BORRADOR, NO PRESENTADA,
            SUSPENDIDA, ANULADA, DESISTIDA). El estado activo queda sombreado
            en su color asociado para que sea claramente visible sin scroll. */}
        <div className={`border-b border-gray-200 px-4 py-2.5 flex flex-wrap items-center gap-2 ${
          (ESTADO_COLOR[estado] || "bg-gray-50 text-gray-800 border-gray-200").split(" ").slice(0,2).join(" ")
        }`} data-testid="oferta-estado-topbar">
          <span className="text-[10px] uppercase font-bold tracking-wide opacity-70">Estado licitación ⚑</span>
          {ESTADOS_ALTA.map(e => (
            <button
              key={e}
              type="button"
              onClick={()=>{
                setForm(p => {
                  const next = {...p, estado_licitacion: e};
                  if (e === "PRESENTADA" && !p.fase_adjudicacion) next.fase_adjudicacion = "PTE_APERTURA";
                  return next;
                });
              }}
              className={`text-xs px-3 py-1 rounded-full border font-semibold transition ${
                estado === e
                  ? `${ESTADO_COLOR[e]} shadow-sm ring-1 ring-inset ring-black/5`
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`estado-${e}`}
            >{e}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[10px] uppercase font-semibold text-gray-500">Cambiar estado</label>
            <select
              value={ESTADOS_TRANSICION.includes(estado) ? estado : ""}
              onChange={e => {
                const val = e.target.value;
                if (!val) return;
                setForm(p => ({...p, estado_licitacion: val}));
              }}
              data-testid="estado-transicion-select"
              className="text-[11px] px-2 py-1 rounded border border-gray-300 bg-white hover:border-gray-400"
            >
              <option value="">— seleccionar —</option>
              {ESTADOS_TRANSICION.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>
        </div>

        <ProjectLinkBanner oferta={form} onLinked={(project) => setForm(p => ({ ...p, auto_project_id: project?.id, auto_project_created_at: project?.created_at }))} />

        {/* Section tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50 overflow-x-auto">
          {[
            { k: "base", l: "Datos base", show: true },
            { k: "responsables", l: "Responsables", show: true },
            { k: "sub",  l: showSubNoPres ? "Motivo no pres." : showSubPipeline ? "Fase de adjudicación" : "Sub-bloque", show: true },
            { k: "apertura", l: "Apertura", show: showApertura },
            { k: "ue",   l: "Units Economics", show: showUE },
            { k: "comentarios", l: "Observaciones", show: true },
            { k: "timeline", l: "Línea temporal", show: !isNew },
            { k: "cumplimiento", l: "Matriz de cumplimiento", show: !isNew },
          ].filter(s => s.show).map(s => (
            <button key={s.k} onClick={()=>setSection(s.k)}
                    className={`px-4 py-2 text-xs font-semibold whitespace-nowrap ${section === s.k ? "border-b-2 border-[#007934] text-[#007934]" : "text-gray-500 hover:text-gray-700"}`}
                    data-testid={`form-tab-${s.k}`}>
              {s.l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {section === "base" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <F label="Cliente *"><I v={form.cliente} on={v=>update("cliente",v)} list="ac-cliente"/></F>
              <F label="Expediente"><I v={form.expediente} on={v=>update("expediente",v)}/></F>
              <F label="Publicación"><I v={form.publicacion} on={v=>update("publicacion",v)} list="ac-publicacion"/></F>

              <F label="País"><I v={form.pais} on={v=>update("pais",v)}/></F>
              <F label="Provincia"><I v={form.provincia} on={v=>update("provincia",v)} list="ac-provincia"/></F>
              <F label="Fecha alta"><I type="date" v={form.fecha_alta} on={v=>update("fecha_alta",v)}/></F>

              <F label="Empresa G.Valoriza"><I v={form.empresa_g_valoriza} on={v=>update("empresa_g_valoriza",v)} list="ac-empresa-g-valoriza"/></F>
              <F label={form.ute ? "Empresa oferta (auto desde socios UTE)" : "Empresa oferta"}>
                <I v={form.empresa_oferta} on={v=>update("empresa_oferta",v)} list="ac-empresa-oferta"
                   readOnly={form.ute} title={form.ute ? "Se rellena automáticamente desde el editor de socios UTE" : undefined}
                   style={form.ute ? { background: "#f9fafb", color: "#6b7280" } : undefined}/>
              </F>

              <F label="Procedimiento"><I v={form.procedimiento} on={v=>update("procedimiento",v)}/></F>
              <F label="Dirección general">
                <select value={form.direccion_general} onChange={e => {
                  const newDg = e.target.value;
                  update("direccion_general", newDg);
                  // Si la actividad actual no encaja en la nueva DG → resetear
                  const allowed = (catalogs.actividades_por_dg || {})[newDg] || [];
                  if (form.actividad_comercial && !allowed.includes(form.actividad_comercial)) {
                    update("actividad_comercial", allowed[0] || "");
                  }
                }} className="w-full border rounded px-2 py-1.5 bg-white">
                  {catalogs.direccion_general.map(o => <option key={o}>{o}</option>)}
                </select>
              </F>
              <F label="Dirección zona"><I v={form.direccion_zona} on={v=>update("direccion_zona",v)} list="ac-direccion-zona"/></F>

              <F label="Gerencia"><I v={form.gerencia} on={v=>update("gerencia",v)} list="ac-gerencia"/></F>
              <F label="Tipo cliente">
                <select value={form.tipo_cliente} onChange={e=>update("tipo_cliente",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white">
                  {catalogs.tipo_cliente.map(o => <option key={o}>{o}</option>)}
                </select>
              </F>

              <F label="Área negocio">
                <select value={form.area_negocio || ""} onChange={e=>update("area_negocio",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white">
                  <option value="">—</option>
                  {catalogs.area_negocio.map(o => <option key={o}>{o}</option>)}
                </select>
              </F>
              <F label="Actividad comercial">
                <select value={form.actividad_comercial || ""} onChange={e=>update("actividad_comercial",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white">
                  <option value="">—</option>
                  {((catalogs.actividades_por_dg || {})[form.direccion_general] || catalogs.actividad_comercial).map(o => <option key={o}>{o}</option>)}
                </select>
              </F>
              {/* Responsable comercial visible al crear/editar (antes solo en su
                  pestaña). Sugerencias incluyen al equipo (usuarios dados de alta). */}
              <F label="Responsable comercial" span={3}>
                <MultiTagInput
                  value={form.responsables}
                  onChange={v => update("responsables", v)}
                  suggestions={autocomplete?.equipo?.length ? autocomplete.equipo : (autocomplete?.responsables || [])}
                  testid="oc-responsables-base"
                  placeholder="Escribe o elige del equipo…"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">Elige del equipo o escribe un nombre y pulsa Enter. También disponible en la pestaña «Responsables».</p>
              </F>

              <F label="Objeto contrato" span={3}>
                <textarea value={form.objeto_contrato || ""} onChange={e=>update("objeto_contrato",e.target.value)} rows={2} className="w-full border rounded px-2 py-1.5"/>
              </F>

              {/* ═══ Iter228 · Tipo contratación + UTE (movidos arriba, sobre Presupuesto,
                    para que el usuario los defina antes de introducir importes) ═══ */}
              <F label="Tipo contratación (NC/R/ADC)">
                <select value={form.tipo_contratacion || ""} onChange={e=>update("tipo_contratacion", e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white" data-testid="oc-tipo-contratacion">
                  <option value="">— auto según incumbent —</option>
                  {(catalogs.tipo_contratacion || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <p className="text-[9px] text-gray-500 mt-0.5">NC/R se deriva automáticamente del incumbent. ADC es manual.</p>
              </F>
              <F label="¿Se presenta en UTE?" span={form.ute ? 2 : 1}>
                <div className="flex items-center gap-3 mt-1">
                  <label className="inline-flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!form.ute} onChange={e=>update("ute", e.target.checked)} data-testid="oc-ute-toggle"/>
                    UTE
                  </label>
                  {form.ute && (
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-gray-500">% VSM</span>
                      <input type="number" step="0.01" min="0" max="100" value={form.porcentaje_ute ?? 100} onChange={e=>update("porcentaje_ute", parseFloat(e.target.value))} className="w-16 border rounded px-1 py-0.5" data-testid="oc-porcentaje-ute"/>
                    </div>
                  )}
                </div>
                {form.ute && form.presupuesto && (
                  <p className="text-[9px] text-emerald-700 mt-0.5">
                    Importe con % UTE ≈ {new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format((form.presupuesto || 0) * (form.porcentaje_ute || 100) / 100)}
                  </p>
                )}
                {form.ute && Number(form.porcentaje_ute ?? 100) === 100 && (
                  <p className="text-[9px] text-amber-700 mt-0.5" data-testid="oc-ute-100-note">
                    ⚠ VSM al 100%: formalmente marcada UTE pero sin reparto con socios → es una <b>licitación individual efectiva</b>. No se cuenta como agrupación hasta que haya otro socio con % &gt; 0.
                  </p>
                )}
                {/* Iter176 · Editor visual de socios UTE (bloquea si suma ≠ 100) */}
                {form.ute && (
                  <UtePartnersEditor
                    empresa_oferta={form.empresa_oferta}
                    porcentaje_ute={form.porcentaje_ute}
                    sociasSuggestions={autocomplete?.empresas_socias || []}
                    onChange={({ empresa_oferta, porcentaje_ute }) => {
                      setForm(p => ({ ...p, empresa_oferta, porcentaje_ute }));
                    }}
                  />
                )}
              </F>

              <F label="Presupuesto (€)"><I type="number" step="0.01" v={form.presupuesto} on={v=>update("presupuesto",v)}/></F>
              {/* Iter183 · Renombrado "Presup. con %UTE (M€)" → "M€ con UTE"
                  con tooltip claro; era ambiguo con la etiqueta previa. */}
              <F label="M€ con UTE" title="Importe correspondiente aplicando % en UTE (en millones)">
                <I type="number" step="0.01" v={form.presupuesto_licitacion_ute_mm} on={v=>update("presupuesto_licitacion_ute_mm",v)}/>
              </F>
              <F label="Presup. anual con %UTE">
                <I type="number" step="0.01" v={form.presupuesto_anual_ute} on={v=>update("presupuesto_anual_ute",v)}/>
                <p className="text-[9px] text-gray-500 mt-0.5">Importe anual tal como se importó. Puede NO coincidir con Presupuesto ÷ años si el pliego mezcla inversión inicial, anualidades irregulares, opciones o prórrogas — se conserva el valor original, no se recalcula.</p>
              </F>

              <F label="Duración (años)">
                <I type="number" v={form.duracion_anios} on={v=>update("duracion_anios",v)}/>
                <p className="text-[9px] text-gray-500 mt-0.5">Plazo inicial del contrato, en años. El plazo total ≈ duración + prórroga (la prórroga va aparte con su propia unidad).</p>
              </F>
              <F label="Prórroga (meses)">
                <I type="number" v={form.prorroga} on={v=>update("prorroga",v)}/>
                <p className="text-[9px] text-gray-500 mt-0.5">En MESES. Se guarda tal cual se importó; si el pliego la da en años, conviértela (1 año = 12 meses). Indica la unidad real en «Formato prórrogas».</p>
              </F>
              <F label="Formato prórrogas"><I v={form.formato_prorrogas} on={v=>update("formato_prorrogas",v)} list="ac-formato-prorrogas"/></F>

              {/* Iter202 · Eliminado campo duplicado "Zona": ya existe "Dirección
                  zona" en el bloque superior con desplegable. */}

              <F label="Fecha y hora límite presentación">
                <div className="flex gap-1.5">
                  <div className="flex-1"><I type="date" v={form.fecha_presentacion} on={v=>update("fecha_presentacion",v)}/></div>
                  <div className="w-24"><I type="time" v={form.hora_limite_presentacion} on={v=>update("hora_limite_presentacion",v)}/></div>
                </div>
              </F>
              <F label="Límite preguntas"><I v={form.limite_preguntas} on={v=>update("limite_preguntas",v)}/></F>
              <F label="Central/Zona"><I v={form.central_zona} on={v=>update("central_zona",v)} list="ac-central-zona"/></F>

              {/* Iter185 · Market y size trasladados desde Observaciones a Datos base */}
              <F label="Market">
                <select value={form.market || ""} onChange={e=>update("market",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white" data-testid="oc-base-market">
                  {catalogs.market_type.map(o => <option key={o}>{o}</option>)}
                </select>
              </F>
              <F label="Size > 20 M€">
                <select value={form.size_gt_20 || ""} onChange={e=>update("size_gt_20",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white" data-testid="oc-base-size">
                  <option value="">—</option><option>SI</option><option>NO</option>
                </select>
              </F>

              {/* Iter185 · Estado licitación se muestra en la topbar destacada (fuera del tab
                  "Datos base") — se elimina el bloque duplicado que había aquí. */}

              {/* Panel embebido: sugerencias de fusión con PLACSP (iter 106) */}
              <OfertaMergeSuggestionsPanel oferta={data} onLinked={onSaved} />
            </div>
          )}

          {/* Iter193/206 · Sección dedicada "Responsables".
              Aquí conviven: responsables comerciales, ganadores designados,
              equipo interno y colaboradores externos si aplica.
              Iter206 · Gerencia y Dirección zona removidas (ya viven en
              Datos base). Estudia (equipo) y Colaboradores externos ahora
              son multiselect con creación libre. */}
          {section === "responsables" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid="section-responsables">
              <F label="Responsables comercial *" span={2}>
                <MultiTagInput
                  value={form.responsables}
                  onChange={v => update("responsables", v)}
                  suggestions={autocomplete?.responsables || []}
                  testid="oc-responsables-multi"
                  placeholder="Añade responsables comerciales…"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">Se usa para auto-vincular con Gestión de Proyectos. Puedes crear nuevos escribiéndolos y pulsando Enter.</p>
              </F>
              <F label="Estudia (equipo)" span={2}>
                <MultiTagInput
                  value={form.estudia_equipo}
                  onChange={v => update("estudia_equipo", v)}
                  suggestions={autocomplete?.responsables || []}
                  testid="oc-estudia-equipo-multi"
                  placeholder="Añade miembros del equipo de estudio…"
                />
              </F>
              {/* Iter202 · Firmante Contrato con desplegable oficial para
                  SERVICIOS (equipo firmante conocido). Para TRATAMIENTO y
                  otras direcciones se permite texto libre porque el firmante
                  varía por proyecto. */}
              <F label="Firmante Contrato">
                {String(form.direccion_general || "").toUpperCase() === "SERVICIOS" ? (
                  <select
                    value={form.firmante || ""}
                    onChange={e=>update("firmante", e.target.value)}
                    className="w-full border rounded px-2 py-1.5 bg-white"
                    data-testid="oc-firmante-select">
                    <option value="">—</option>
                    <option value="Angel Gonzalez">Angel Gonzalez</option>
                    <option value="Antonio Gutierrez">Antonio Gutierrez</option>
                    <option value="Jose Luis Salegui">Jose Luis Salegui</option>
                    <option value="Juan Carlos Uzal">Juan Carlos Uzal</option>
                    <option value="__otro__">Otro (escribir)…</option>
                  </select>
                ) : (
                  <I v={form.firmante} on={v=>update("firmante", v)} list="ac-responsables" data-testid="oc-firmante-text"/>
                )}
                {form.firmante === "__otro__" && (
                  <I v="" on={v=>update("firmante", v)} data-testid="oc-firmante-otro" placeholder="Nombre del firmante"/>
                )}
                <p className="text-[9px] text-gray-500 mt-0.5">
                  {String(form.direccion_general || "").toUpperCase() === "SERVICIOS"
                    ? "Servicios: firmante oficial del equipo directivo."
                    : "Texto libre — depende del proyecto y dirección."}
                </p>
              </F>
              <F label="Colaboradores externos" span={2}>
                <MultiTagInput
                  value={form.colaboradores_externos}
                  onChange={v => update("colaboradores_externos", v)}
                  suggestions={autocomplete?.colaboradores_externos || []}
                  testid="oc-colaboradores-multi"
                  placeholder="Ingenierías, consultoras, subcontratas…"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">Se guarda como texto libre separado por comas. Añade nuevos escribiéndolos y pulsando Enter.</p>
              </F>
            </div>
          )}

          {section === "sub" && showSubNoPres && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid="sub-no-pres">
              <F label="Motivo no presentación *">
                <select value={form.motivo_no_presentacion || ""} onChange={e=>update("motivo_no_presentacion",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white">
                  <option value="">— Elegir —</option>
                  {catalogs.motivo_no_presentacion.map(o => (
                    <option key={o} value={o}>
                      {/* Iter182 · "Non-strategic" → "Sin interés" en UI */}
                      {(catalogs.motivo_no_presentacion_label || {})[o] || o}
                    </option>
                  ))}
                </select>
              </F>
              <F label="Observaciones" span={2}>
                <textarea value={form.observaciones || ""} onChange={e=>update("observaciones",e.target.value)} rows={4} className="w-full border rounded px-2 py-1.5"/>
              </F>
            </div>
          )}

          {section === "sub" && showSubPipeline && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="sub-pipeline">
              <F label="Fecha apertura"><I type="date" v={form.fecha_apertura} on={v=>update("fecha_apertura",v)}/></F>
              <F label="Fecha req. adjudicación"><I type="date" v={form.fecha_req_adjudicacion} on={v=>update("fecha_req_adjudicacion",v)}/></F>
              <F label="Fecha doc. adjudicación"><I type="date" v={form.fecha_doc_adjudicacion} on={v=>update("fecha_doc_adjudicacion",v)}/></F>
              <F label="Fecha adjudicación"><I type="date" v={form.fecha_adjudicacion} on={v=>update("fecha_adjudicacion",v)}/></F>
              <F label="Fecha límite recurso"><I type="date" v={form.fecha_limite_recurso} on={v=>update("fecha_limite_recurso",v)}/></F>
              <F label="Fecha límite alegaciones"><I type="date" v={form.fecha_limite_alegaciones} on={v=>update("fecha_limite_alegaciones",v)}/></F>
              <F label="Fecha límite firma contrato"><I type="date" v={form.fecha_limite_firma_contrato} on={v=>update("fecha_limite_firma_contrato",v)}/></F>
              <F label="Fecha firma contrato">
                <I
                  type="date"
                  v={form.fecha_firma_contrato}
                  on={v=>update("fecha_firma_contrato",v)}
                  max={new Date().toISOString().slice(0,10)}
                  data-testid="oferta-fecha-firma-contrato"
                />
                {form.fase_adjudicacion === "FIRMADA" && form.fecha_firma_contrato && (() => { const n = new Date(); const t = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`; return String(form.fecha_firma_contrato).slice(0,10) > t; })() && (
                  <div className="mt-1 text-[10px] text-rose-600 font-semibold" data-testid="oferta-fecha-firma-futura-warning">
                    ⚠ FIRMADA no admite fecha futura. ¿Querías la fecha límite de firma?
                  </div>
                )}
              </F>
              <F label="Fecha inicio servicio"><I type="date" v={form.fecha_inicio_servicio} on={v=>update("fecha_inicio_servicio",v)}/></F>

              <F label="Fase adjudicación">
                <select value={form.fase_adjudicacion || ""} onChange={e=>update("fase_adjudicacion",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white" data-testid="oc-fase-adj">
                  <option value="">—</option>
                  {catalogs.fase_adjudicacion.map(o => (
                    <option key={o} value={o}>
                      {(catalogs.fase_adjudicacion_label || {})[o] || o}
                    </option>
                  ))}
                </select>
              </F>
              {/* Iter203 · Detalle de fase acotado según el mapa oficial del
                  negocio (spec del usuario, imagen 04/08/2026).
                  Cualquier cambio aquí requiere revisar con el usuario. */}
              <F label="Detalle fase">
                <select value={form.detalle_fase_adjudicacion || ""} onChange={e=>update("detalle_fase_adjudicacion",e.target.value)} className="w-full border rounded px-2 py-1.5 bg-white" data-testid="oc-detalle-fase">
                  <option value="">—</option>
                  {(() => {
                    // Mapeo OFICIAL fase → detalles válidos (imagen 04/08/2026)
                    const DETALLES_POR_FASE = {
                      "PTE_APERTURA":         ["SUSPENDIDA", "PTE_CONOCER_ADJUDICATARIO", "PTE_ECONÓMICA"],
                      "CLASIFICADOS_1":       ["PTE_JBT", "PTE_REQ_ADJ", "PTE_DOC_ADJ", "PTE_ADJUDICAR"],
                      "ADJUDICADA":           ["PTE_PLAZO RECURSO", "PTE_ALEGACIONES", "PTE_RESOLUCIÓN RECURSO", "PTE_ACUERDO MESA CONT.", "PTE_FIRMA"],
                      "ADJUDICADA_OTROS":     ["PTE_PLAZO RECURSO", "PTE_RESOLUCIÓN RECURSO", "PTE_ACUERDO MESA CONT."],
                      // Fases sin detalle propio (según spec oficial)
                      "FIRMADA":              [],
                      "PTE_ADJUDICAR_OTROS":  [],
                      "NO_ADJUDICADA":        [],
                      "ANULADA":              [],
                      "DESISTIDA":            [],
                    };
                    const fase = form.fase_adjudicacion || "";
                    const permitidos = DETALLES_POR_FASE[fase];
                    // Sin fase elegida: mostrar TODOS (para retrocompatibilidad
                    // con ofertas legacy que no tengan fase pero sí detalle).
                    const opciones = permitidos != null
                      ? permitidos
                      : catalogs.detalle_fase;
                    return opciones.map(o => <option key={o} value={o}>{o}</option>);
                  })()}
                </select>
                {form.fase_adjudicacion && (() => {
                  const map = {
                    "PTE_APERTURA": 3, "CLASIFICADOS_1": 4, "ADJUDICADA": 5,
                    "ADJUDICADA_OTROS": 3, "FIRMADA": 0, "PTE_ADJUDICAR_OTROS": 0,
                    "NO_ADJUDICADA": 0, "ANULADA": 0, "DESISTIDA": 0,
                  };
                  const n = map[form.fase_adjudicacion];
                  return (
                    <p className="text-[9px] text-gray-500 mt-0.5">
                      {n === 0
                        ? <>Fase terminal · <b>sin detalle</b> aplicable.</>
                        : <>Detalles filtrados para <b>{form.fase_adjudicacion}</b> ({n ?? "?"} opciones). Cambia la fase para ver otras.</>}
                    </p>
                  );
                })()}
              </F>
              {/* Iter182 · Presupuesto ofertado sólo tiene sentido en fase
                  PTE_APERTURA (todavía no se ha publicado el importe ganador). */}
              {form.fase_adjudicacion === "PTE_APERTURA" && (
                <F label="Presupuesto ofertado (€)"><I type="number" v={form.presupuesto_ofertado} on={v=>update("presupuesto_ofertado",v)}/></F>
              )}
              <F label="Nº licitadores"><I type="number" v={form.num_licitadores} on={v=>update("num_licitadores",v)}/></F>
            </div>
          )}

          {section === "sub" && !showSubNoPres && !showSubPipeline && (
            <div className="text-xs text-gray-500 italic p-6 text-center">
              Selecciona en <strong>Datos base</strong> el estado PRESENTADA o NO PRESENTADA para habilitar el sub-bloque correspondiente.
            </div>
          )}

          {section === "apertura" && (
            <div className="space-y-4" data-testid="section-apertura">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-900 font-semibold">Puntuaciones y posición tras apertura pública</p>
                <p className="text-[10px] text-amber-700">Rellena estos campos cuando la mesa publique el acta.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <F label="Fecha apertura"><I type="date" v={form.apertura_fecha} on={v=>update("apertura_fecha",v)} data-testid="oc-ap-fecha"/></F>
                <F label="Nº total licitadores"><I type="number" v={form.apertura_num_total_licitadores} on={v=>update("apertura_num_total_licitadores", parseInt(v)||null)} data-testid="oc-ap-num-licitadores"/></F>
                <F label="Presupuesto ofertado VSM (€)"><I type="number" step="0.01" v={form.apertura_presupuesto_ofertado} on={v=>update("apertura_presupuesto_ofertado", parseFloat(v)||null)}/></F>
                <F label="">{/* spacer */}<span/></F>
              </div>

              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-[11px] font-bold text-gray-700 mb-2 uppercase">Posición VSM</h4>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <F label="Pos. TEC"><I type="number" min="1" v={form.apertura_posicion_vsm_tec} on={v=>update("apertura_posicion_vsm_tec", parseInt(v)||null)} data-testid="oc-ap-pos-tec"/></F>
                  <F label="Pos. ECO"><I type="number" min="1" v={form.apertura_posicion_vsm_eco} on={v=>update("apertura_posicion_vsm_eco", parseInt(v)||null)}/></F>
                  <F label="Pos. TOT"><I type="number" min="1" v={form.apertura_posicion_vsm_tot} on={v=>update("apertura_posicion_vsm_tot", parseInt(v)||null)} data-testid="oc-ap-pos-tot"/></F>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-blue-200 p-3 bg-blue-50/30">
                  <h4 className="text-[11px] font-bold text-blue-900 mb-2 uppercase">Puntuación pliego (máxima)</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <F label="Máx. TEC"><I type="number" step="0.01" v={form.apertura_puntuacion_pliego_tec} on={v=>update("apertura_puntuacion_pliego_tec", parseFloat(v)||null)}/></F>
                    <F label="Máx. ECO"><I type="number" step="0.01" v={form.apertura_puntuacion_pliego_eco} on={v=>update("apertura_puntuacion_pliego_eco", parseFloat(v)||null)}/></F>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 p-3 bg-emerald-50/30">
                  <h4 className="text-[11px] font-bold text-emerald-900 mb-2 uppercase">Puntuación VSM</h4>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <F label="TEC"><I type="number" step="0.01" v={form.apertura_puntuacion_vsm_tec} on={v=>update("apertura_puntuacion_vsm_tec", parseFloat(v)||null)} data-testid="oc-ap-vsm-tec"/></F>
                    <F label="ECO"><I type="number" step="0.01" v={form.apertura_puntuacion_vsm_eco} on={v=>update("apertura_puntuacion_vsm_eco", parseFloat(v)||null)}/></F>
                    <F label="TOT"><I type="number" step="0.01" v={form.apertura_puntuacion_vsm_tot} on={v=>update("apertura_puntuacion_vsm_tot", parseFloat(v)||null)} data-testid="oc-ap-vsm-tot"/></F>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-yellow-200 p-3 bg-yellow-50/30">
                  <h4 className="text-[11px] font-bold text-yellow-900 mb-2 uppercase">🏆 Empresa WINNER</h4>
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <F label="Empresa"><I v={form.apertura_empresa_winner} on={v=>update("apertura_empresa_winner", v)} list="ac-winner" data-testid="oc-ap-empresa-winner"/></F>
                    <div className="grid grid-cols-3 gap-2">
                      <F label="TEC"><I type="number" step="0.01" v={form.apertura_puntuacion_winner_tec} on={v=>update("apertura_puntuacion_winner_tec", parseFloat(v)||null)}/></F>
                      <F label="ECO"><I type="number" step="0.01" v={form.apertura_puntuacion_winner_eco} on={v=>update("apertura_puntuacion_winner_eco", parseFloat(v)||null)}/></F>
                      <F label="TOT"><I type="number" step="0.01" v={form.apertura_puntuacion_winner_tot} on={v=>update("apertura_puntuacion_winner_tot", parseFloat(v)||null)}/></F>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-rose-200 p-3 bg-rose-50/30">
                  <h4 className="text-[11px] font-bold text-rose-900 mb-2 uppercase">Máximas puntuaciones (referencias)</h4>
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="grid grid-cols-2 gap-2">
                      <F label="Empresa máx. TEC"><I v={form.apertura_empresa_maxima_tec} on={v=>update("apertura_empresa_maxima_tec", v)}/></F>
                      <F label="Puntos"><I type="number" step="0.01" v={form.apertura_maxima_puntuacion_tec} on={v=>update("apertura_maxima_puntuacion_tec", parseFloat(v)||null)}/></F>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <F label="Empresa máx. ECO"><I v={form.apertura_empresa_maxima_eco} on={v=>update("apertura_empresa_maxima_eco", v)}/></F>
                      <F label="Puntos"><I type="number" step="0.01" v={form.apertura_maxima_puntuacion_eco} on={v=>update("apertura_maxima_puntuacion_eco", parseFloat(v)||null)}/></F>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {section === "ue" && (
            <div className="space-y-3" data-testid="section-ue">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-xs text-indigo-900 font-semibold">UNITS ECONOMICS · Modelo financiero del contrato</p>
                <p className="text-[10px] text-indigo-700">Rellena estos campos una vez la oferta tenga modelo aprobado. Todos son opcionales.</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                <F label="CAPEX (€)"><I type="number" step="0.01" v={form.ue_capex} on={v=>update("ue_capex", parseFloat(v)||null)} data-testid="oc-ue-capex"/></F>
                <F label="EBITDA (€)"><I type="number" step="0.01" v={form.ue_ebitda} on={v=>update("ue_ebitda", parseFloat(v)||null)} data-testid="oc-ue-ebitda"/></F>
                <F label="Gross Margin (%)"><I type="number" step="0.01" v={form.ue_gross_margin} on={v=>update("ue_gross_margin", parseFloat(v)||null)}/></F>
                <F label="IRR unlevered (%)"><I type="number" step="0.01" v={form.ue_irr_unlevered} on={v=>update("ue_irr_unlevered", parseFloat(v)||null)} data-testid="oc-ue-irr"/></F>
                <F label="MOIC (múltiplo)"><I type="number" step="0.01" v={form.ue_moic} on={v=>update("ue_moic", parseFloat(v)||null)}/></F>
                <F label="EBT (€)"><I type="number" step="0.01" v={form.ue_ebt} on={v=>update("ue_ebt", parseFloat(v)||null)}/></F>
              </div>
              {(form.ue_capex || form.ue_ebitda || form.ue_moic) && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                  {form.ue_capex && <KPIcard label="CAPEX" value={new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(form.ue_capex)} color="bg-blue-50 border-blue-200 text-blue-900"/>}
                  {form.ue_ebitda && <KPIcard label="EBITDA" value={new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(form.ue_ebitda)} color="bg-emerald-50 border-emerald-200 text-emerald-900"/>}
                  {form.ue_gross_margin && <KPIcard label="Gross Margin" value={`${form.ue_gross_margin}%`} color="bg-indigo-50 border-indigo-200 text-indigo-900"/>}
                  {form.ue_moic && <KPIcard label="MOIC" value={`${form.ue_moic}x`} color="bg-purple-50 border-purple-200 text-purple-900"/>}
                </div>
              )}
            </div>
          )}

          {section === "comentarios" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="section-comentarios">
              {/* Iter185 · Sección Observaciones limpia:
                  - Plataforma + Link se mantienen (metadatos de la publicación externa)
                  - Market y Size trasladados a Datos base
                  - Winner/Winner 2026 sólo si la fase de adjudicación lo requiere */}
              <F label="Plataforma"><I v={form.plataforma} on={v=>update("plataforma",v)}/></F>
              <F label="Link" span={2}><I v={form.link} on={v=>update("link",v)}/></F>
              <F label="Incumbent"><I v={form.incumbent} on={v=>update("incumbent",v)} list="ac-incumbent"/></F>
              <F label="Fuente Incumbent" span={2}><I v={form.fuente_incumbent} on={v=>update("fuente_incumbent",v)}/></F>
              {showWinner && (
                <>
                  <F label="Winner"><I v={form.winner} on={v=>update("winner",v)} list="ac-winner"/></F>
                  <F label="Winner presentadas 2026" span={2}><I v={form.winner_presentadas_2026} on={v=>update("winner_presentadas_2026",v)} list="ac-winner"/></F>
                </>
              )}
              <F label="Observaciones" span={3}>
                <textarea value={form.observaciones || ""} onChange={e=>update("observaciones",e.target.value)} rows={4} className="w-full border rounded px-2 py-1.5"/>
              </F>
              <F label="Otros comentarios" span={3}>
                <textarea value={form.otros_comentarios || ""} onChange={e=>update("otros_comentarios",e.target.value)} rows={3} className="w-full border rounded px-2 py-1.5"/>
              </F>
            </div>
          )}

          {section === "timeline" && (
            <div data-testid="section-timeline">
              <LicitacionTimeline oid={data?.id} />
            </div>
          )}
          {section === "cumplimiento" && (
            <div data-testid="section-cumplimiento">
              <ComplianceMatrix oid={data?.id} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 p-4 border-t border-gray-100 bg-gray-50">
          {!isNew && canDelete && (
            <button onClick={handleDelete} data-testid="oc-form-delete-btn" title="Enviar a la papelera (recuperable)"
              className="text-xs text-rose-600 border border-rose-200 px-3 py-1.5 rounded flex items-center gap-1 hover:bg-rose-50">
              <Trash2 size={12}/> Eliminar
            </button>
          )}
          <div className="flex-1"/>
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-4 py-1.5 rounded flex items-center gap-1 hover:bg-[#006528] disabled:opacity-40" data-testid="oc-save-btn">
            {saving && <Loader2 className="animate-spin" size={12}/>}
            {isNew ? "Crear" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children, span = 1, title }) {
  return (
    <div className={span === 3 ? "md:col-span-3" : span === 2 ? "md:col-span-2" : ""}>
      <div
        className={`text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 ${title ? "cursor-help underline decoration-dotted" : ""}`}
        title={title}
      >{label}</div>
      {children}
    </div>
  );
}
function I({ v, on, type = "text", step, ...rest }) {
  // Las fechas importadas se guardan en ISO con hora ("2026-01-14T00:00:00")
  // y <input type="date"> sólo acepta "YYYY-MM-DD" → se mostraba en blanco y
  // parecía que "no se guardaban". Normalizamos el valor mostrado al formato
  // que el input entiende (date→10 chars, time→5 chars).
  const val = type === "date" ? String(v ?? "").slice(0, 10)
            : type === "time" ? String(v ?? "").slice(0, 5)
            : (v ?? "");
  return <input type={type} step={step} value={val} onChange={e=>on(e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs" {...rest}/>;
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter176 · UTE partners: parse/compile + editor visual
// ═══════════════════════════════════════════════════════════════════════════

/** Parsea `empresa_oferta` "VSM/FCC/Prezero" a filas [{name,pct}] usando
 * `porcentaje_ute` como % de VSM. El resto se reparte a partes iguales.
 * Si sólo hay 1 socio (o vacío), devuelve VSM al 100%. */
function parseUtePartners(empresa_oferta, porcentaje_ute) {
  const raw = String(empresa_oferta || "").trim();
  if (!raw) return [{ name: "VSM", pct: 100 }];
  const parts = raw.split(/[/\-]/).map(s => s.trim()).filter(Boolean);
  if (parts.length <= 1) return [{ name: parts[0] || "VSM", pct: 100 }];
  const vsmPct = Number(porcentaje_ute);
  const validVsm = Number.isFinite(vsmPct) && vsmPct > 0 && vsmPct <= 100;
  const vsmIdx = parts.findIndex(p => /^vsm$/i.test(p) || /valoriza/i.test(p));
  const rows = parts.map((name, i) => ({ name, pct: 0 }));
  if (validVsm && vsmIdx >= 0) {
    rows[vsmIdx].pct = vsmPct;
    const rest = (100 - vsmPct) / (rows.length - 1);
    rows.forEach((r, i) => { if (i !== vsmIdx) r.pct = Math.round(rest * 100) / 100; });
  } else {
    // Sin % conocido, reparto igualitario
    const each = Math.round((100 / rows.length) * 100) / 100;
    rows.forEach(r => { r.pct = each; });
  }
  return rows;
}

/** Compila filas [{name,pct}] de vuelta a "VSM/FCC/Prezero" (nombres únicos,
 * en orden). Devuelve también el % de VSM para `porcentaje_ute`. */
function compileUtePartners(rows) {
  const clean = rows
    .map(r => ({ name: String(r.name || "").trim(), pct: Number(r.pct) || 0 }))
    .filter(r => r.name.length > 0);
  const names = clean.map(r => r.name).join("/");
  const vsm = clean.find(r => /^vsm$/i.test(r.name) || /valoriza/i.test(r.name));
  return { empresa_oferta: names, porcentaje_ute: vsm ? vsm.pct : (clean[0]?.pct ?? 100) };
}

function UtePartnersEditor({ empresa_oferta, porcentaje_ute, sociasSuggestions = [], onChange }) {
  // Estado local para permitir edición fluida sin re-parsear en cada tecla
  const [rows, setRows] = useState(() => parseUtePartners(empresa_oferta, porcentaje_ute));

  // Sincroniza fuera del componente cada vez que cambian las filas
  useEffect(() => {
    const compiled = compileUtePartners(rows);
    onChange(compiled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const total = rows.reduce((s, r) => s + (parseFloat(r.pct) || 0), 0);
  const okSum = Math.abs(total - 100) < 0.01;

  const setRow = (i, k, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(prev => [...prev, { name: "", pct: 0 }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const distribute = () => {
    // Deja el % de VSM tal cual y reparte el resto entre los demás.
    const vsmIdx = rows.findIndex(r => /^vsm$/i.test(r.name) || /valoriza/i.test(r.name));
    if (rows.length < 2) return;
    if (vsmIdx >= 0 && (Number(rows[vsmIdx].pct) || 0) > 0) {
      const vsmPct = Number(rows[vsmIdx].pct);
      const rest = (100 - vsmPct) / (rows.length - 1);
      setRows(prev => prev.map((r, i) => i === vsmIdx ? r : { ...r, pct: Math.round(rest * 100) / 100 }));
    } else {
      const each = Math.round((100 / rows.length) * 100) / 100;
      setRows(prev => prev.map(r => ({ ...r, pct: each })));
    }
  };

  return (
    <div className="mt-2 border border-amber-200 bg-amber-50/40 rounded-lg p-3 space-y-2"
         data-testid="ute-partners-editor">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold text-amber-800 tracking-wide">Socios UTE · edita nombres y % de cada uno</div>
        <button type="button" onClick={distribute}
                data-testid="ute-distribute-btn"
                className="text-[10px] text-indigo-700 hover:underline">Repartir automáticamente</button>
      </div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`ute-partner-row-${i}`}>
            <input
              type="text" value={r.name}
              onChange={e => setRow(i, "name", e.target.value)}
              list="ute-socias-list"
              placeholder={i === 0 ? "VSM" : "Socio…"}
              data-testid={`ute-partner-name-${i}`}
              className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs bg-white"/>
            <input
              type="number" step="0.01" min="0" max="100"
              value={r.pct} onChange={e => setRow(i, "pct", e.target.value)}
              data-testid={`ute-partner-pct-${i}`}
              className="w-20 border border-gray-200 rounded px-2 py-1 text-xs bg-white"/>
            <span className="text-[10px] text-gray-500 w-3">%</span>
            {rows.length > 1 && (
              <button type="button" onClick={() => removeRow(i)}
                      data-testid={`ute-partner-remove-${i}`}
                      className="text-rose-500 hover:text-rose-700 text-xs px-1" title="Eliminar socio">×</button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <button type="button" onClick={addRow} data-testid="ute-add-partner-btn"
                className="text-emerald-700 font-semibold hover:underline">+ Añadir socio</button>
        <span data-testid="ute-partner-sum"
              className={`font-mono font-bold ${okSum ? "text-emerald-700" : "text-rose-700"}`}>
          Suma: {total.toFixed(2)}% {okSum ? "✓" : `(debe ser 100)`}
        </span>
      </div>
      <datalist id="ute-socias-list">
        {sociasSuggestions.map(s => <option key={s} value={s}/>)}
      </datalist>
    </div>
  );
}
function KPIcard({ label, value, color }) {
  return (
    <div className={`border rounded-lg px-2 py-1.5 text-center ${color}`}>
      <div className="text-[9px] uppercase opacity-70 font-semibold">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter206 · MultiTagInput: input tipo "chips" con sugerencias de BD.
// El valor externo es un string separado por comas (compat legacy).
// Sugerencias vienen de autocomplete.responsables / colaboradores_externos.
// ═══════════════════════════════════════════════════════════════════════════
function MultiTagInput({ value, onChange, suggestions = [], placeholder = "", testid }) {
  const parseTags = (raw) => String(raw || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);
  const [tags, setTags] = useState(() => parseTags(value));
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  // Sync interno cuando cambia value desde fuera (ej. reset)
  useEffect(() => {
    const parsed = parseTags(value);
    if (parsed.join("|") !== tags.join("|")) setTags(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = (arr) => {
    setTags(arr);
    onChange(arr.join(", "));
  };
  const addTag = (t) => {
    const clean = t.trim();
    if (!clean) return;
    if (tags.some(x => x.toLowerCase() === clean.toLowerCase())) return;
    commit([...tags, clean]);
    setInput("");
  };
  const removeTag = (idx) => {
    commit(tags.filter((_, i) => i !== idx));
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length) {
      commit(tags.slice(0, -1));
    }
  };
  const filtered = suggestions
    .filter(s => s && !tags.some(t => t.toLowerCase() === String(s).toLowerCase()))
    .filter(s => !input || String(s).toLowerCase().includes(input.toLowerCase()))
    .slice(0, 8);
  return (
    <div className="relative" data-testid={testid}>
      <div className="w-full min-h-[34px] border rounded px-2 py-1 flex flex-wrap gap-1 items-center bg-white focus-within:border-[#007934]">
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 text-[11px]"
                data-testid={`${testid}-tag-${i}`}>
            {t}
            <button type="button" onClick={() => removeTag(i)} className="text-emerald-600 hover:text-red-600" aria-label={`Quitar ${t}`}>×</button>
          </span>
        ))}
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={tags.length ? "" : placeholder}
          className="flex-1 min-w-[120px] outline-none border-none text-xs py-1 bg-transparent"
          data-testid={`${testid}-input`}
        />
      </div>
      {focused && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto"
             data-testid={`${testid}-suggestions`}>
          {filtered.map(s => (
            <button
              key={s}
              type="button"
              onMouseDown={() => addTag(s)}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Iter152 · Exports públicos (los helpers F, I, KPIcard, PendingMotivoModal quedan internos)
export { OfertaForm, PendingMotivoModal, PendingConfirmacionPresentacionModal, ImportFromExternalModal };
